import { useState, useMemo } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import type {
  ParsedFundWaterfallResult,
  WaterfallViewType,
} from '@/hooks/react-query/useDistributionWaterfall';
import { WaterfallMetricsGrid } from './WaterfallMetricsGrid';
import { WaterfallTable } from './WaterfallTable';

interface WaterfallContentProps {
  result: ParsedFundWaterfallResult;
  currency: string;
  viewType: WaterfallViewType;
}

export type WaterfallMetric =
  | 'called_capital'
  | 'total_distribution_amount'
  | 'preferred_return_amount'
  | 'total_lp'
  | 'total_gp'
  | 'total_profit'
  | null;

export function WaterfallContent({
  result,
  currency,
  viewType,
}: WaterfallContentProps) {
  const [selectedMetric, setSelectedMetric] = useState<WaterfallMetric>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const overall = result?.overall;

  const filteredInvestors = useMemo(() => {
    if (!result?.investors) return [];
    return result.investors.filter((lp) =>
      lp.investor_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [result.investors, searchTerm]);

  if (!overall) {
    return (
      <div className="text-center py-8 text-muted-foreground border rounded-lg border-dashed">
        No calculation data available.
      </div>
    );
  }

  const isTheoretical = viewType === 'unrealised';

  const handleCardClick = (metric: WaterfallMetric) => {
    setSelectedMetric((prev) => (prev === metric ? null : metric));
  };

  return (
    <>
      <WaterfallMetricsGrid
        overall={overall}
        currency={currency}
        isTheoretical={isTheoretical}
        selectedMetric={selectedMetric}
        onCardClick={handleCardClick}
        totalLp={overall.total_lp}
        totalGp={overall.total_gp}
        totalProfit={overall.total_profit}
      />

      <div className="space-y-3 pt-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">
            Investor Waterfall Breakdown
          </h3>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search investors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-64 h-8 text-sm"
            />
          </div>
        </div>

        <WaterfallTable
          investors={filteredInvestors}
          currency={currency}
          selectedMetric={selectedMetric}
          isTheoretical={isTheoretical}
          searchTerm={searchTerm}
          overall={overall}
          totalLp={overall.total_lp}
          totalGp={overall.total_gp}
          totalProfit={overall.total_profit}
        />
      </div>
    </>
  );
}
